
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useMainLinks } from '@/hooks/use-main-links';
import { CreateMainLinkData } from '@/types/link-short';

interface CreateMainLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateMainLinkDialog({ open, onOpenChange }: CreateMainLinkDialogProps) {
  const { createMainLink } = useMainLinks();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateMainLinkData>({
    title: '',
    slug: '',
    description: '',
    bg_color: '#3b82f6',
    text_color: '#ffffff'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await createMainLink(formData);
      if (result) {
        onOpenChange(false);
        setFormData({
          title: '',
          slug: '',
          description: '',
          bg_color: '#3b82f6',
          text_color: '#ffffff'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug === '' ? generateSlug(title) : prev.slug
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Criar Novo Link</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Meu Perfil"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">URL Personalizada *</Label>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">/{window.location.host}/</span>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="meu-perfil"
                pattern="^[a-z0-9-]+$"
                title="Apenas letras minúsculas, números e hífens"
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Apenas letras minúsculas, números e hífens
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Uma breve descrição sobre você"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bg_color">Cor de Fundo</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="bg_color"
                  type="color"
                  value={formData.bg_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                  className="w-12 h-10 p-1 rounded"
                />
                <Input
                  value={formData.bg_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="text_color">Cor do Texto</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="text_color"
                  type="color"
                  value={formData.text_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                  className="w-12 h-10 p-1 rounded"
                />
                <Input
                  value={formData.text_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Link'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
